import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useField } from 'react-final-form';
import { RepairType } from '../../components/RepairType';
import { FORM_VALUES } from '../../../../../utils/helpers/constants';
import { checkIsFlatCategory } from '../../../../../utils/helpers/common';
import { FormBlockWrapper } from '../FormBlockWrapper';
import { BuildingType } from '../../components/BuildingType';
import { FormTextInput } from '../../components/FormTextInput';

interface AboutEstateProps {
  isEdit?: boolean;
}

const AboutEstateComponent: React.FC<AboutEstateProps> = ({
  isEdit = false
}) => {
  const { t } = useTranslation();

  const { input: categoryIdField } = useField(FORM_VALUES.categoryId, {
    subscription: { value: true }
  });
  const { input: categoryValuesField } = useField(FORM_VALUES.categoryValues, {
    subscription: { value: true }
  });

  const categoryValues = categoryValuesField.value || {};
  const isFlat = checkIsFlatCategory(categoryIdField.value);
  const categoryHasBuildingType = categoryValues.hasBuildingType;

  const formatAreaValue = (value = '') =>
    value.toString().replace(/(\.0?)?$/, '');

  const areaLabel = categoryValues.hasLandArea
    ? t('new_ad.building_area')
    : t('search.form.area');
  const areaPlaceholder = `${areaLabel} ${categoryValues.areaUnits} *`;

  const roomsPlaceholder = categoryValues.areRoomsRequired
    ? t('new_ad.rooms_amount')
    : t('new_ad.rooms_amount').replace(' *', '');

  return (
    <FormBlockWrapper header={t('new_ad.about_estate')} required>
      {!isEdit && categoryHasBuildingType && <BuildingType isFlat={isFlat} />}

      {categoryValues.hasRooms && (
        <FormTextInput
          name={FORM_VALUES.roomsAmount}
          placeholder={roomsPlaceholder}
          inputMode="numeric"
          isEnabledAutoSelectionRange={false}
          hasErrorField
          isFloat={false}
        />
      )}

      <FormTextInput
        name={FORM_VALUES.area}
        placeholder={areaPlaceholder}
        format={formatAreaValue}
        inputMode="numeric"
        isEnabledAutoSelectionRange={false}
        hasErrorField
        isFloat
      />

      {categoryValues.hasLandArea && (
        <FormTextInput
          name={FORM_VALUES.landArea}
          inputMode="numeric"
          format={formatAreaValue}
          isEnabledAutoSelectionRange={false}
          placeholder={`${t('new_ad.total_land_area')} *`}
          hint={t('new_ad.total_land_area_hint')}
          hasErrorField
          isFloat
        />
      )}
      {(categoryValues.hasFloor || isFlat) && (
        <>
          <FormTextInput
            inputMode="numeric"
            name={FORM_VALUES.floor}
            isEnabledAutoSelectionRange={false}
            placeholder={`${t('search.form.floor')} *`}
            hasErrorField
          />

          <FormTextInput
            inputMode="numeric"
            name={FORM_VALUES.totalFloors}
            placeholder={t('new_ad.total_floors')}
            isEnabledAutoSelectionRange={false}
            hasErrorField
          />
        </>
      )}

      {categoryValues.hasRepair && <RepairType />}
    </FormBlockWrapper>
  );
};

export const AboutEstate = memo(AboutEstateComponent);
